import { useState } from 'react';
import { AlertCircle, CheckCircle2, Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { api, extractErrorMessage } from '../../lib/api';

/**
 * Page `/reporting/prestataires` (US client — reporting prestataire).
 *
 * Déclenchement manuel du rapport mensuel / trimestriel par prestataire :
 * agrège les interventions clôturées de la période, génère un Excel déposé sur
 * MinIO, notifie la direction (notif + email) et renvoie un lien de
 * téléchargement signé. La génération automatique reste assurée par le cron
 * mensuel (worker BullMQ) — cette page sert au déclenchement à la demande.
 */

interface ReportResult {
  period: string;
  label: string;
  prestataire_count: number;
  total_interventions: number;
  recipients: number;
  download_url: string | null;
  pdf_download_url: string | null;
}

function currentYear(): number {
  // Pas de Date.now() côté logique métier : on dérive l'année du champ natif.
  return new Date().getFullYear();
}

export function PrestataireReportPage() {
  const year = currentYear();
  const [type, setType] = useState<'month' | 'quarter'>('month');
  const [month, setMonth] = useState('01');
  const [quarter, setQuarter] = useState('1');
  const [selectedYear, setSelectedYear] = useState(String(year));
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [result, setResult] = useState<ReportResult | null>(null);

  const years = [year, year - 1, year - 2].map(String);

  async function generate() {
    setLoading(true);
    setErrorMessage(null);
    setResult(null);
    const period = type === 'month' ? `${selectedYear}-${month}` : `${selectedYear}-Q${quarter}`;
    try {
      const res = await api.post<{ data: ReportResult }>('/reporting/prestataire-report', {
        period,
      });
      setResult(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <FileSpreadsheet className="w-7 h-7 mr-2 text-primary-600" />
          Rapport prestataires
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Synthèse des interventions clôturées par prestataire sur une période : répartition curatif
          / préventif, délai moyen d'intervention (curatif), part couverte / non couverte par
          contrat de maintenance, coût additionnel non couvert (main d'œuvre + pièces) et détail par
          intervention. Livré en Excel (exhaustif) et PDF (diffusion), déposé sur le stockage
          sécurisé avec notification de la direction. Génération automatique : 1er de chaque mois.
        </p>
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Périodicité</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as 'month' | 'quarter')}
              className={inputClass}
            >
              <option value="month">Mensuel</option>
              <option value="quarter">Trimestriel</option>
            </select>
          </div>

          {type === 'month' ? (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Mois</label>
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className={inputClass}
              >
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Trimestre</label>
              <select
                value={quarter}
                onChange={(e) => setQuarter(e.target.value)}
                className={inputClass}
              >
                <option value="1">T1 (jan–mar)</option>
                <option value="2">T2 (avr–juin)</option>
                <option value="3">T3 (juil–sept)</option>
                <option value="4">T4 (oct–déc)</option>
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Année</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className={inputClass}
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={generate}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4 mr-2" />
            )}
            Générer le rapport
          </button>
        </div>

        {result && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-2">
            <p className="text-sm font-medium text-green-800 flex items-center">
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              Rapport « {result.label} » généré
            </p>
            <ul className="text-sm text-gray-700 space-y-0.5 ml-6 list-disc">
              <li>{result.prestataire_count} prestataire(s)</li>
              <li>{result.total_interventions} intervention(s) clôturée(s)</li>
              <li>{result.recipients} destinataire(s) notifié(s) (direction)</li>
            </ul>
            <div className="flex flex-wrap gap-2">
              {result.download_url && (
                <a
                  href={result.download_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium text-primary-700 bg-white border border-primary-300 hover:bg-primary-50"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  Télécharger l'Excel
                </a>
              )}
              {result.pdf_download_url && (
                <a
                  href={result.pdf_download_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium text-red-700 bg-white border border-red-300 hover:bg-red-50"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  Télécharger le PDF
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const MONTHS = [
  { value: '01', label: 'Janvier' },
  { value: '02', label: 'Février' },
  { value: '03', label: 'Mars' },
  { value: '04', label: 'Avril' },
  { value: '05', label: 'Mai' },
  { value: '06', label: 'Juin' },
  { value: '07', label: 'Juillet' },
  { value: '08', label: 'Août' },
  { value: '09', label: 'Septembre' },
  { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Décembre' },
];

const inputClass =
  'px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
